import BackBtn from '@/components/buttons/back/BackBtn';
import { generateGetProfileData } from '@/hooks/useProfile';
import { useMediaQuery } from '@mui/material';
import ProfileLayout from 'layouts/ProfileLayout/PersonalProfileLayout';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useMemo, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { getProfile } from '../../../../apis/profile.api';
import handleShare from '../../../../common/handleShare';
import { detechBuildProfileKey } from '../../../../common/parseProfileInfo';
import { socialMediaData } from '../../../../constant/user-setting-modal-resources';
import * as ProfileInterface from '../../../../interfaces/profile.interface';
import {
  FollowModalState,
  ModalType,
} from '../../../../interfaces/profile.interface';
import NotFound from '../../../../pages/404';
import AuthContext from '../../../../state/auth/AuthContext';
import { useModal } from '../../../../state/modal-provider/ModalProvider';
import RenderUserSettingModal from '../../../modal/user-setting';
import TabPanel from '../../../tabs/TabPanel';
import UserAvt from '../../UserAvt';
import CoinReward from '../CoinReward';
import { FullName } from '../detail-each-profile-info';
import * as ProfileElementSkeleton from '../skeleton';
import SwitchTabProfile from '../switch-tab/SwitchTabProfile';

const FeedTab = dynamic(() => import('../switch-tab/FeedTab'), {
  loading: () => <ProfileElementSkeleton.FeedTabSkeleton />,
});
const FollowModal = dynamic(() => import('../../../modal/follow/follow'), {
  loading: () => <ProfileElementSkeleton.FollowModalSkeleton />,
});
const ThreeDotToggleModal = dynamic(
  () => import('../../../modal/ThreeDotToggleModal'),
  { loading: () => <ProfileElementSkeleton.ThreeDotToggleModalSkeleton /> }
);
const ProfileTab = dynamic(() => import('../switch-tab/ProfileTab'), {
  loading: () => <ProfileElementSkeleton.ProfileTabSkeleton />,
});
const FollowSection = dynamic(() => import('../sections/FollowSection'), {
  loading: () => <ProfileElementSkeleton.FollowSectionSkeleton />,
});
const FollowListSwitchTab = dynamic(
  () => import('../switch-tab/FollowListSwitchTab'),
  { loading: () => <ProfileElementSkeleton.FollowListSwitchTabSkeleton /> }
);
const ContactSection = dynamic(() => import('../sections/ContactSection'), {
  loading: () => <ProfileElementSkeleton.ContactSectionSkeleton />,
});

export const initModal: ModalType = {
  isOpen: false,
  name: '',
  value: {},
};

let currentSwiperSlide = 0;

const PersonalProfileMode = ({
  initData,
  slug,
}: {
  initData: ProfileInterface.IUserProfileData;
  slug: string;
}) => {
  const router = useRouter();
  const uid = initData?.profiles?.ownerId.toString();

  if (!initData) return <NotFound />;
  const [data, setData] = useState(initData);

  const [enumPage, setEnumPage] = useState<number>(0);

  const matches = useMediaQuery('(min-width:1024px');

  const { setLoading } = useContext(AuthContext);
  const { toast } = useModal();

  const [modal, setModal] = useState<ModalType>(initModal);

  const [isShowFollowModal, setIsShowFollowModal] = useState<FollowModalState>({
    isShow: false,
    followType: ProfileInterface.FollowPageEnum.Follower,
  });

  const [bottomModal, setBottomModal] = useState<boolean>(false);
  const [imageModal, setImageModal] = useState<boolean>(false);

  const roleView = data?.viewPermission;

  const profileDocumentInfo = useMemo(
    () => ({
      project: data?.profiles.project,
      volunteeringActivity: data?.profiles.volunteeringActivity,
      bio: data?.profiles.bio,
      school: data?.profiles.school,
      experience: data?.profiles.experience,
      certificate: data?.profiles.certificate,
      reward: data?.profiles.reward,
      scientific: data?.profiles.scientific,
    }),
    [data]
  );

  const profileContactInfo = useMemo(
    () => ({
      social: data?.profiles.social.filter(
        (item: any) => item.socialNetwork != socialMediaData.length
      ),
      website: data?.profiles.social.filter(
        (item: any) => item.socialNetwork == socialMediaData.length
      ),
      contact: data?.profiles.contact,
    }),
    [data]
  );

  const incompleteProfiles = useMemo(() => {
    const incompleteProfiles = [];
    if (!data?.profiles.avatar) {
      incompleteProfiles.push('avatar');
    }
    Object.keys(data?.profiles).forEach((_key) => {
      const key = _key as keyof typeof data.profiles;
      if (
        detechBuildProfileKey(key) &&
        (data?.profiles[key] as Object[])?.length == 0 &&
        (key as string) !== 'social-link'
      )
        incompleteProfiles.push(key);
    });
    if (profileContactInfo.social.length <= 0) {
      incompleteProfiles.push('social-link');
    }
    if (profileContactInfo.website.length <= 0) {
      incompleteProfiles.push('website');
    }
    return incompleteProfiles;
  }, [data, profileContactInfo]);

  const followers = data?.profiles.isFollowedBy;
  const following = data?.profiles.followers;
  const isFollowedMe = data?.profiles.isFollowedMe;

  const handleToggleBottomModal = () => {
    setBottomModal((state) => !state);
  };

  const refectAfterChangeInformation = async () => {
    setLoading(true);
    currentSwiperSlide = 0;
    try {
      const res = await getProfile(
        generateGetProfileData({
          userId: uid ?? '',
        })
      );
      setData(res.data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const cancelModal = (refetch: boolean) => {
    if (refetch) currentSwiperSlide = 0;
    setModal(initModal);
    refetch && refectAfterChangeInformation();
  };

  const handleToggleFollowModal = (
    isShowValue: boolean,
    followTypeValue?: ProfileInterface.FollowPageType
  ) => {
    setIsShowFollowModal((state) => ({
      ...state,
      isShow: isShowValue,
      followType: followTypeValue || ProfileInterface.FollowPageEnum.Follower,
    }));
  };

  // ---------------------------add function above this line------------------------------ //
  return (
    <RenderUserSettingModal modal={modal} cancelModal={cancelModal}>
      {isShowFollowModal.isShow ? (
        <FollowModal
          roleView={roleView}
          followType={isShowFollowModal.followType}
          fullname={data?.profiles?.qbankData?.fullName}
          toggleModal={() => handleToggleFollowModal(false)}
          userId={uid}
          handleRefetch={refectAfterChangeInformation}
          followerLength={
            following?.filter((eachUser: any) => eachUser.isAccepted).length
          }
          isFollowedByLength={
            followers?.filter((eachUser: any) => eachUser.isAccepted).length
          }
        />
      ) : (
        <ProfileLayout
          roleView={roleView}
          identify={uid}
          isDisplayed={
            roleView === ProfileInterface.RoleViewEnum.Owner ? false : true
          }
          showHeader={true}
          showSearchHeader={!data?.profiles.pendingFollowMe}
          childrenClassname="p-6 pb-[50px] lg:py-0 lg:px-10 relative"
          // props for header
          info={data?.profiles.qbankData}
          data={data}
          slug={slug}
          handleShare={handleShare}
          handleToggleBottomModal={handleToggleBottomModal}
          uid={uid}
          refectAfterChangeInformation={refectAfterChangeInformation}
        >
          {!(roleView == ProfileInterface.RoleViewEnum.Owner) && (
            <div className="absolute bottom-full left-5 z-10 mb-5 hidden xl:flex">
              <BackBtn callback={() => router.back()} size={20} />
            </div>
          )}
          {bottomModal && (
            <ThreeDotToggleModal
              fullName={data?.profiles.qbankData?.fullName}
              isFollowedMe={isFollowedMe}
              toggleModal={handleToggleBottomModal}
              handleShare={handleShare}
              uid={uid}
              refectAfterChangeInformation={refectAfterChangeInformation}
            />
          )}

          <div className="relative">
            {/* mobile: top right corner icons */}
            <div className="absolute right-2 top-1 flex sm:hidden">
              {/* change bottom modal button to share button*/}
              <div
                className="ml-2 cursor-pointer"
                onClick={() =>
                  handleShare(data?.profiles.qbankData?.fullName, toast)
                }
              >
                <Image
                  alt="share"
                  src="/icons/share-blue.svg"
                  width={20}
                  height={20}
                />
              </div>
            </div>
            {/* desktop: top right corner icons */}
            <div className="absolute right-2 top-6 hidden sm:flex">
              {roleView !== ProfileInterface.RoleViewEnum.Owner ? (
                <div>
                  <div
                    className="ml-2 cursor-pointer sm:hidden"
                    onClick={handleToggleBottomModal}
                  >
                    <BsThreeDotsVertical size={24} />
                  </div>
                  <div
                    onClick={() =>
                      handleShare(data?.profiles.qbankData?.fullName, toast)
                    }
                    className="hidden cursor-pointer justify-between rounded-lg bg-[#EFFBFF]/70 px-2 py-1 hover:bg-[#EFFBFF] sm:flex"
                  >
                    <Image
                      src={'/icons/hyperlink-white.svg'}
                      alt="copy url icon"
                      width={17}
                      height={17}
                    />
                    <div className="pl-1 font-light">Chia sẻ</div>
                  </div>
                </div>
              ) : (
                <div
                  className="ml-2 flex cursor-pointer justify-between rounded-lg bg-[#EFFBFF]/70 px-2 py-1 hover:bg-[#EFFBFF]"
                  onClick={() =>
                    handleShare(data?.profiles.qbankData?.fullName, toast)
                  }
                >
                  <Image
                    src={'/icons/hyperlink-white.svg'}
                    alt="copy url icon"
                    width={17}
                    height={17}
                  />
                  <div className="pl-1 font-light">Chia sẻ</div>
                </div>
              )}
            </div>
          </div>
          <div className="h-screen-app pb-10">
            <div className="lg:mt-5 lg:grid lg:grid-cols-2 ">
              <div className="mx-auto flex flex-col lg:mx-0 lg:items-start">
                <UserAvt
                  imageModal={imageModal}
                  setImageModal={setImageModal}
                  imgSrc={data?.profiles?.avatar || ''}
                  viewPermission={roleView}
                />
                <FullName
                  data={{
                    info: data?.profiles.qbankData,
                    title: data?.profiles.title,
                    hasBlueTick: data?.profiles?.hasBlueTick,
                  }}
                  setModal={setModal}
                  roleView={roleView}
                />
              </div>
              <div className="lg:mr-0 lg:flex lg:max-w-[50vw] lg:items-end lg:justify-center">
                <FollowListSwitchTab
                  followers={followers}
                  following={following}
                  handleToggleFollowModal={handleToggleFollowModal}
                />
              </div>

              <div className="lg:hidden">
                {roleView === ProfileInterface.RoleViewEnum.Owner && (
                  <CoinReward />
                )}
              </div>
            </div>

            <FollowSection
              refectAfterChangeInformation={refectAfterChangeInformation}
              isAccepted={data?.isAccepted || false}
              uid={uid}
              roleView={roleView}
            />

            <div>
              <SwitchTabProfile
                matches={matches}
                enumPage={enumPage}
                setEnumPage={setEnumPage}
              />
              <div>
                <TabPanel value={enumPage} index={0}>
                  <FeedTab
                    isOwnerProfile={
                      initData.viewPermission ===
                      ProfileInterface.RoleViewEnum.Owner
                    }
                    email={data?.profiles?.qbankData?.email}
                    fullName={data?.profiles?.qbankData?.fullName}
                    userId={uid}
                  />
                </TabPanel>
              </div>
              <TabPanel value={enumPage} index={1}>
                <ProfileTab
                  incompleteProfiles={incompleteProfiles}
                  profileInfo={profileDocumentInfo}
                  contactInfo={profileContactInfo}
                  modal={modal}
                  setModal={setModal}
                  roleView={roleView}
                  setImageModal={setImageModal}
                  currentSwiperSlide={currentSwiperSlide}
                  refectAfterChangeInformation={refectAfterChangeInformation}
                />
              </TabPanel>
              {!matches && (
                <TabPanel value={enumPage} index={2}>
                  <ContactSection
                    roleView={roleView}
                    setModal={setModal}
                    {...profileContactInfo}
                  />
                </TabPanel>
              )}
            </div>
          </div>
        </ProfileLayout>
      )}
    </RenderUserSettingModal>
  );
};

export default PersonalProfileMode;
